"use client"

import { JSX, useCallback } from "react"
import type { TokenUsageResponse } from "@/services/resourceUsageService"
import StackedBarChart from "../shared/StackedBarChart"
import {
  colorForAgent,
  toChartData,
  formatDayLabel,
  formatAgentLabel,
  type TokenMetric,
} from "./tokenUsageUtils"

// The three metrics, each rendered as its own chart, with display titles.
const METRIC_CHARTS: ReadonlyArray<{ metric: TokenMetric; title: string }> = [
  { metric: "totalTokens", title: "Total tokens" },
  { metric: "inputTokens", title: "Input tokens" },
  { metric: "outputTokens", title: "Output tokens" },
]

/**
 * Props for the TokenUsageCharts component.
 */
export interface TokenUsageChartsProps {
  /** The token usage response to visualise. */
  usage: TokenUsageResponse
}

/**
 * TokenUsageCharts renders the three per-metric stacked bar charts
 * (total / input / output) with a per-chart agent legend beside each chart.
 *
 * Shared by the personal and admin token usage pages; the response shape is the
 * same for both (the admin response is org-wide aggregates), so the charts are
 * identical.
 *
 * @param props - See TokenUsageChartsProps.
 * @returns The three charts stacked vertically.
 */
export default function TokenUsageCharts({
  usage,
}: TokenUsageChartsProps): JSX.Element {
  // Memoised x-axis label formatter so the chart dependency is stable.
  const formatXLabel = useCallback(
    (isoDate: string) => formatDayLabel(isoDate),
    []
  )

  return (
    <div className="flex flex-col gap-8">
      {METRIC_CHARTS.map(({ metric, title }) => (
        <div key={metric} className="flex flex-col gap-1">
          <h2 className="text-sm font-medium">{title}</h2>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            {/* Chart, capped so it doesn't stretch across the page. */}
            <div className="w-full max-w-2xl min-w-0">
              <StackedBarChart
                data={toChartData(usage, metric)}
                seriesKeys={usage.agents}
                colorFor={colorForAgent}
                formatXLabel={formatXLabel}
                formatSeriesLabel={formatAgentLabel}
                valueUnit="tokens"
              />
            </div>
            {/* Per-chart legend, beside the chart on wider screens. */}
            <ul
              className="flex flex-row flex-wrap gap-x-4 gap-y-2 sm:flex-col sm:gap-2"
              aria-label={`${title} agent legend`}
            >
              {usage.agents.map((agent) => (
                <li key={agent} className="flex items-center gap-2">
                  <span
                    className="inline-block size-3 rounded-sm"
                    style={{ backgroundColor: colorForAgent(agent) }}
                    aria-hidden="true"
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatAgentLabel(agent)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </div>
  )
}
